import { useState } from "react";

const faqs = [
  {
    q: "Who can invest in the Thorpe Dellow Social Housing Structured Product?",
    a: "This product is intended solely for persons who are Certified High Net Worth Individuals, Self-Certified Sophisticated Investors, or retail investors where explicitly permitted and subject to an appropriateness assessment. If you are unsure of your classification, please seek independent financial advice before proceeding.",
  },
  {
    q: "What is the target return and how is it paid?",
    a: "The product targets a return of 10.00% per annum, distributed monthly on a pro rata basis. Returns are generated from rental income produced by the underlying UK assisted living assets leased to a registered housing association. For example, a £100,000 investment would target approximately £833.33 per month.",
  },
  {
    q: "What happens to my capital at the end of the 5-year term?",
    a: "At maturity, investors may elect to receive full capital repayment, subject to the performance of the underlying assets. Alternatively, investors may choose to roll over into a subsequent investment term, subject to terms available at the time of maturity.",
  },
  {
    q: "How is my investment secured?",
    a: "Investor capital is secured by first legal charges over all underlying properties. An Independent Security Trustee is appointed to hold this security on behalf of investors, monitor ongoing compliance throughout the term, and enforce security where required. This structure is designed to protect investor interests throughout the investment period.",
  },
  {
    q: "What is a Special Purpose Vehicle (SPV) and why is it used?",
    a: "A Special Purpose Vehicle (SPV) is a ring-fenced legal entity established solely for the purpose of acquiring and holding the underlying property assets. Using an SPV isolates the investment assets from any other business activities, providing a cleaner structure for investor security and asset management.",
  },
  {
    q: "What is an FRI lease and why does it matter?",
    a: "An FRI (Full Repair and Insurance) lease means the housing association tenant is responsible for all maintenance, repairs, and insurance of the property — not the SPV or investors. This structure removes significant operational costs and risks from the investment, helping to protect the income stream flowing to investors.",
  },
  {
    q: "How are rents linked to inflation?",
    a: "The lease agreements with the housing association include index-linked rent increases set at CPI (Consumer Price Index) plus 1% per annum. This means rental income — and therefore investor distributions — have the potential to grow in line with inflation over the course of the investment term.",
  },
  {
    q: "Are there void periods where no rent is received?",
    a: "No. The properties are subject to long-term lease agreements (up to 25 years) with a UK-registered housing association. The structure provides assured rental income with no void periods, as the housing association is contractually obligated to pay rent regardless of individual unit occupancy.",
  },
  {
    q: "Can I exit the investment before the 5-year term ends?",
    a: "This is an illiquid investment. There is no secondary market for this product, and investors should expect to hold their investment for the full 5-year term. You should only invest capital you are comfortable committing for the full duration. If liquidity is a priority, this product may not be appropriate for you.",
  },
  {
    q: "What are the tax implications of investing?",
    a: "The structure is designed to be tax efficient; however, tax treatment depends entirely on your individual circumstances and may vary. The returns you receive may be subject to income tax, capital gains tax, or other levies depending on your personal tax position. Thorpe Dellow strongly recommends that all investors seek independent tax advice prior to investing.",
  },
  {
    q: "How are investor funds used?",
    a: "Investor capital is used for three primary purposes: acquisition of UK assisted living properties through the SPV, payment of transaction costs including legal fees and stamp duty land tax, and the establishment of income-generating assets under long-term housing association leases.",
  },
  {
    q: "What due diligence should I conduct before investing?",
    a: "You should read the full Information Memorandum carefully, ensure you understand all risks outlined in the Risk Summary, confirm your eligibility as a qualified investor, seek independent financial advice, and seek independent tax advice. Our investment team is available to answer any questions prior to your commitment.",
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section className="bg-white py-20 md:py-24">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <span className="text-[#C9A84C] text-xs font-semibold uppercase tracking-widest block mb-3">
            Common Questions
          </span>
          <h2
            className="text-3xl md:text-4xl font-bold text-[#071A2E]"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Frequently Asked Questions
          </h2>
          <p className="text-slate-500 text-sm mt-3 max-w-lg mx-auto">
            Answers to the most common questions we receive from prospective investors. For anything not covered here, please contact our team directly.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {faqs.map((faq, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={i}
                className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
                  isOpen
                    ? "border-[#C9A84C]/40 bg-[#EEF2F9]"
                    : "border-slate-100 bg-[#EEF2F9] hover:border-slate-200"
                }`}
              >
                <button
                  onClick={() => toggle(i)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer"
                >
                  <span
                    className={`text-sm font-semibold leading-snug transition-colors ${
                      isOpen ? "text-[#C9A84C]" : "text-[#071A2E]"
                    }`}
                  >
                    {faq.q}
                  </span>
                  <div
                    className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-200 ${
                      isOpen
                        ? "bg-[#C9A84C] text-[#071A2E] rotate-45"
                        : "bg-white text-slate-400"
                    }`}
                  >
                    <i className="ri-add-line text-sm leading-none" />
                  </div>
                </button>

                <div
                  className={`transition-all duration-300 ease-in-out ${
                    isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                  } overflow-hidden`}
                >
                  <div className="px-6 pb-6">
                    <div className="border-t border-slate-200 pt-4">
                      <p className="text-slate-600 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-10 bg-[#071A2E] rounded-2xl p-8 text-center">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#C9A84C]/20 mx-auto mb-4">
            <i className="ri-question-answer-line text-[#C9A84C] text-xl leading-none" />
          </div>
          <h3
            className="font-bold text-white text-lg mb-2"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Still Have Questions?
          </h3>
          <p className="text-slate-400 text-sm mb-6 max-w-sm mx-auto">
            Our investment team is available Monday to Friday, 9am–5pm GMT, to answer any questions before you commit.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center flex-wrap">
            <a
              href="mailto:invest@thorpedellow.com"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#C9A84C] text-[#071A2E] text-sm font-semibold hover:bg-[#B8953B] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-line leading-none" />
              invest@thorpedellow.com
            </a>
            <a
              href="tel:+442030976185"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-white/20 text-white text-sm font-semibold hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line leading-none" />
              +44 20 3097 6185
            </a>
            <a
              href="tel:+14372922296"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-white/20 text-white text-sm font-semibold hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line leading-none" />
              +1 437 292 2296
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
